import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RotatingText.css';

const RotatingText = ({
  texts = [],
  rotationInterval = 2000,
  initial = { y: '100%', opacity: 0 },
  animate = { y: 0, opacity: 1 },
  exit = { y: '-120%', opacity: 0 },
  animatePresenceMode = 'wait',
  animatePresenceInitial = false,
  staggerDuration = 0.025,
  staggerFrom = 'first',
  transition = { type: 'spring', damping: 25, stiffness: 300 },
  loop = true,
  auto = true,
  splitBy = 'characters',
  onNext,
  mainClassName = '',
  splitLevelClassName = '',
  elementLevelClassName = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const splitText = (text) => {
    if (splitBy === 'characters') {
      return text.split('').map((char, i) => ({
        char: char === ' ' ? '\u00A0' : char,
        key: `${char}-${i}`
      }));
    } else if (splitBy === 'words') {
      return text.split(' ').map((word, i) => ({
        char: word,
        key: `${word}-${i}`
      }));
    }
    return [{ char: text, key: 'full' }];
  };

  const getStaggerDelay = (index, totalChars) => {
    if (staggerFrom === 'last') {
      return (totalChars - 1 - index) * staggerDuration;
    } else if (staggerFrom === 'center') {
      const center = (totalChars - 1) / 2;
      return Math.abs(center - index) * staggerDuration;
    }
    return index * staggerDuration;
  };

  const next = useCallback(() => {
    setCurrentIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= texts.length) {
        return loop ? 0 : prev;
      }
      return nextIndex;
    });
    onNext?.();
  }, [texts.length, loop, onNext]);

  useEffect(() => {
    if (!auto || texts.length <= 1) return;
    const interval = setInterval(next, rotationInterval);
    return () => clearInterval(interval);
  }, [auto, rotationInterval, next, texts.length]);

  if (texts.length === 0) return null;

  const characters = splitText(texts[currentIndex]);

  return (
    <span className={`rotating-text ${mainClassName}`}>
      <AnimatePresence mode={animatePresenceMode} initial={animatePresenceInitial}>
        <motion.span
          key={currentIndex}
          className={`rotating-text-inner ${splitLevelClassName}`}
        >
          {characters.map(({ char, key }, index) => (
            <motion.span
              key={key}
              className={`rotating-char ${elementLevelClassName}`}
              initial={initial}
              animate={animate}
              exit={exit}
              transition={{
                ...transition,
                delay: getStaggerDelay(index, characters.length)
              }}
            >
              {char}
            </motion.span>
          ))}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export default RotatingText;
